/**
 * Three small header pills mounted on the BBB scoring page.
 *
 * SyncPill        — at-a-glance "did my last ball save?" — green / amber / red.
 * IntegrityBadge  — only shows when aggregate.runs ≠ sum of legal-ball runs.
 *                   Silent in normal play; click opens a chat-style dialog that
 *                   suggests a rebuild (the chat panel handles the actual fix).
 * CopyLiveLinkButton — copies the public scorecard URL to the clipboard.
 */
import { useEffect, useState } from "react";
import { Wifi, WifiOff, AlertTriangle, Link2, Check } from "lucide-react";

export function SyncPill({ ballQueue }) {
  const [tickedAt, setTickedAt] = useState(Date.now());

  // Re-render every 5s so "synced 12s ago" stays fresh without external triggers
  useEffect(() => {
    const t = setInterval(() => setTickedAt(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);

  if (!ballQueue) return null;
  const { online, depth, flushing, lastError } = ballQueue;

  if (!online) {
    return (
      <span
        className="flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30"
        title="Offline — your balls are queued locally and will sync when reconnected. Don't refresh."
      >
        <WifiOff className="w-3 h-3" />
        Offline
        {depth > 0 && <span className="font-mono">· {depth} queued</span>}
      </span>
    );
  }

  if (flushing) {
    return (
      <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
        <Wifi className="w-3 h-3 animate-pulse" />
        Syncing… {depth}
      </span>
    );
  }

  if (depth > 0) {
    return (
      <span
        className="flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30"
        title={lastError ? `Last error: ${lastError}` : "Balls waiting to sync"}
      >
        <Wifi className="w-3 h-3" />
        Queued · {depth}
      </span>
    );
  }

  return (
    <span
      className="flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
      title="All balls saved to server"
      data-tick={tickedAt}
    >
      <Wifi className="w-3 h-3" />
      Synced
    </span>
  );
}

// "Saved N · last 4s ago" — a per-session counter giving the scorer concrete
// feedback that their work is reaching the server. Different from SyncPill:
// SyncPill is connectivity, this is throughput.
export function SaveCounterPill({ savedCount, lastSavedAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!savedCount) return null;
  const secs = lastSavedAt ? Math.max(0, Math.floor((now - lastSavedAt.getTime()) / 1000)) : null;
  const label = secs == null ? "" :
    secs < 5  ? "just now" :
    secs < 60 ? `${secs}s ago` :
    secs < 3600 ? `${Math.floor(secs/60)}m ago` :
                  `${Math.floor(secs/3600)}h ago`;
  return (
    <span
      data-testid="bbb-save-counter"
      data-count={savedCount}
      className="flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-bg-secondary text-text-secondary border border-border-default"
      title={`${savedCount} ball${savedCount === 1 ? "" : "s"} saved this session${lastSavedAt ? ` · last save ${label}` : ""}`}
    >
      ✓ {savedCount}
      {label && <span className="font-mono normal-case opacity-80">· {label}</span>}
    </span>
  );
}

// Quick client-side integrity check.
// Note this only catches mismatches when recentBalls covers the entire innings
// (e.g. brand-new innings, fewer than ~12 balls so far). For fuller innings the
// real backend integrity check is the source of truth — this is a tripwire,
// not a guarantee.
export function IntegrityBadge({ aggregate, recentBalls }) {
  if (!aggregate) return null;
  // Only run the cheap front-end check when we have the entire innings cached
  const knownAll = (recentBalls?.length || 0) === (aggregate.legal_balls ?? 0)
                 + (aggregate.illegal_balls ?? 0);
  if (!knownAll) return null;

  const sumRuns = (recentBalls || []).reduce((s, b) => s + (b.runs_total ?? b.runs_off_bat ?? 0), 0);
  const matches = sumRuns === (aggregate.runs ?? 0);
  if (matches) return null;

  return (
    <button
      onClick={() => {
        // Surface the chat panel for repair. The chat hook listens for this.
        try {
          localStorage.setItem("zcd_scorer_chat_open", "1");
          window.dispatchEvent(new CustomEvent("zcd:chat-prompt", {
            detail: { text: "the over count looks wrong, can you fix it" },
          }));
        } catch { /* ignore */ }
      }}
      className="flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30 transition-colors"
      title={`Stats may be off — sum of recent balls = ${sumRuns}, aggregate says ${aggregate.runs}. Click to ask the assistant to fix.`}
    >
      <AlertTriangle className="w-3 h-3" />
      Check stats
    </button>
  );
}

export function CopyLiveLinkButton({ matchId }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    const origin = typeof window !== "undefined" ? window.location.origin : "https://zencricketdata.com";
    const url = `${origin}/cricket/matches/${matchId}/scorecard`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Fallback for older browsers
      window.prompt("Copy this link:", url);
    }
  };
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-bg-secondary text-text-secondary hover:text-text-primary border border-border-default hover:border-button-primary/40 transition-colors"
      title="Copy public live scorecard URL"
    >
      {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Link2 className="w-3 h-3" />}
      {copied ? "Copied" : "Live link"}
    </button>
  );
}
